import { useEffect, useState } from "react";
import CustomLoader from "./CustomLoader";
import api from "../utils/axios";

const BonusAndPaneltyHistory = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState(""); // "" | "debited" | "credited"

  // Modal for reversing a penalty
  const [reversalModal, setReversalModal] = useState({
    isOpen: false,
    item: null,
    reason: "",
  });
  const [reversing, setReversing] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      setError("No token found. Please log in.");
      setLoading(false);
      return;
    }
    fetchTransactionHistory(page, limit, search, filterType);
  }, [page, filterType]);

  const fetchTransactionHistory = async (page, limit, searchKeyword = search, type = filterType) => {
    setLoading(true);
    try {
      const response = await api.get(`/userCreditTransactions`, {
        params: {
          page,
          pageSize: limit,
          search: searchKeyword,
          type: type || undefined,
        },
      });

      const { data, pagination } = response.data;

      setTransactionHistory(data || []);
      setTotalPages(pagination?.totalPages || 1);
      setTotalRecords(pagination?.totalRecords || 0);
      setPage(pagination?.currentPage || 1);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch transaction history:", err);
      setError("Failed to fetch transaction history");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTransactionHistory(1, limit, search, filterType);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  const openReversalModal = (item) => {
    setReversalModal({
      isOpen: true,
      item,
      reason: "",
    });
  };

  const handleConfirmReversal = async () => {
    const { item, reason } = reversalModal;
    if (!item) return;

    if (!reason || reason.trim().length < 5) {
      alert("A clear reason (minimum 5 characters) is required to refund/reverse this penalty.");
      return;
    }

    setReversing(true);
    try {
      const response = await api.post("/admin/reverse-penalty", {
        transactionId: item.id,
        reversalReason: reason.trim(),
      });

      setMessage({
        text: response.data.message || "Penalty successfully reversed and refunded!",
        type: "success",
      });
      setTimeout(() => setMessage(null), 5000);

      setReversalModal({ isOpen: false, item: null, reason: "" });
      fetchTransactionHistory(page, limit, search, filterType);
    } catch (err) {
      console.error("Reversal error:", err);
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || "Failed to reverse penalty";
      setMessage({
        text: errMsg,
        type: "error",
      });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setReversing(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Bonus & Penalty Transaction Audit
            </h1>
            <p className="text-sm text-gray-500">
              Complete immutable ledger of all administrator credits, bonuses, penalties, and reversals.
            </p>
          </div>
          <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg border">
            Total Records: {totalRecords}
          </span>
        </div>

        {message && (
          <div
            className={`mb-4 p-3 rounded-lg text-sm font-semibold border ${
              message.type === "success"
                ? "bg-green-50 text-green-800 border-green-200"
                : "bg-red-50 text-red-800 border-red-200"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Filters and Search Bar */}
        <div className="flex flex-col md:flex-row gap-3 mb-6 items-center justify-between">
          <form onSubmit={handleSearchSubmit} className="flex gap-2 w-full md:w-96">
            <input
              type="text"
              placeholder="Search by User, UID, Email, or Remarks..."
              className="border rounded-lg px-4 py-2 text-sm w-full focus:ring-2 focus:ring-indigo-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
            >
              Search
            </button>
          </form>

          {/* Type Filter Buttons */}
          <div className="inline-flex rounded-lg border bg-white p-1 shadow-sm text-xs font-semibold">
            <button
              onClick={() => { setFilterType(""); setPage(1); }}
              className={`px-3 py-1.5 rounded-md transition ${filterType === "" ? "bg-gray-800 text-white" : "text-gray-600 hover:text-gray-900"}`}
            >
              All Types
            </button>
            <button
              onClick={() => { setFilterType("credited"); setPage(1); }}
              className={`px-3 py-1.5 rounded-md transition ${filterType === "credited" ? "bg-green-600 text-white" : "text-gray-600 hover:text-gray-900"}`}
            >
              Credited (Bonus)
            </button>
            <button
              onClick={() => { setFilterType("debited"); setPage(1); }}
              className={`px-3 py-1.5 rounded-md transition ${filterType === "debited" ? "bg-red-600 text-white" : "text-gray-600 hover:text-gray-900"}`}
            >
              Debited (Penalty)
            </button>
          </div>
        </div>

        {loading ? (
          <CustomLoader />
        ) : error ? (
          <p className="text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">{error}</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
              <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
                <thead className="bg-gray-100 text-gray-700 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">#</th>
                    <th className="px-4 py-3 text-left font-bold">Admin</th>
                    <th className="px-4 py-3 text-left font-bold">Target User</th>
                    <th className="px-4 py-3 text-left font-bold">Amount</th>
                    <th className="px-4 py-3 text-left font-bold">Type</th>
                    <th className="px-4 py-3 text-left font-bold">Remarks & Audit</th>
                    <th className="px-4 py-3 text-left font-bold">Date</th>
                    <th className="px-4 py-3 text-center font-bold">Action / Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-gray-800 divide-y divide-gray-200">
                  {transactionHistory.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-500 font-medium">
                        No transactions found matching your criteria.
                      </td>
                    </tr>
                  ) : (
                    transactionHistory.map((item, index) => {
                      const isDebit = item.type === "debited";
                      const isGc = /gc/i.test(item.remarks || "");
                      const currencySymbol = isGc ? "🪙" : "✨";
                      const currencyCode = isGc ? "GC" : "SC";

                      return (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                            {(page - 1) * limit + index + 1}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900 text-xs">
                              {item.admin_name || `Admin #${item.admin_id}`}
                            </div>
                            <div className="text-[10px] text-gray-400 font-mono">ID: {item.admin_id}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900 text-xs">
                              {item.user_name || "Player"}
                            </div>
                            <div className="text-[11px] text-gray-500 font-mono">UID: {item.user_id}</div>
                            {item.user_email && (
                              <div className="text-[10px] text-gray-400">{item.user_email}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 font-bold whitespace-nowrap">
                            <span className={isDebit ? "text-red-600" : "text-green-600"}>
                              {isDebit ? "-" : "+"}{currencySymbol} {Number(item.amount).toLocaleString(undefined, { minimumFractionDigits: isGc ? 0 : 2, maximumFractionDigits: 2 })} {currencyCode}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                isDebit
                                  ? "bg-red-100 text-red-800 border border-red-200"
                                  : "bg-green-100 text-green-800 border border-green-200"
                              }`}
                            >
                              {isDebit ? "Penalty (Debit)" : "Bonus (Credit)"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs max-w-xs break-words text-gray-700">
                            {item.remarks}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                            {new Date(item.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {isDebit ? (
                              item.is_reversed ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200" title={`Reversal reason: ${item.reversal_reason || "Admin reversed"}`}>
                                  <span>↩️</span> Reversed
                                </span>
                              ) : (
                                <button
                                  onClick={() => openReversalModal(item)}
                                  className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded-md text-xs font-bold transition shadow-sm"
                                  title="Refund / Reverse this penalty"
                                >
                                  Reverse Penalty
                                </button>
                              )
                            ) : (
                              <span className="text-gray-400 text-xs font-medium">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing page <strong>{page}</strong> of <strong>{totalPages}</strong> ({totalRecords} records)
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className={`px-4 py-1.5 rounded-lg text-sm font-semibold border ${
                      page === 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`px-4 py-1.5 rounded-lg text-sm font-semibold border ${
                      page === totalPages ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reversal Confirmation Modal */}
      {reversalModal.isOpen && reversalModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <span className="p-3 rounded-full bg-amber-100 text-amber-700 text-xl font-bold">
                ↩️
              </span>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Reverse Penalty & Refund Balance
                </h3>
                <p className="text-xs text-gray-500">
                  Safely undo this penalty deduction and restore user's balance
                </p>
              </div>
            </div>

            <div className="space-y-2 mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-900">
              <div><strong>Original Transaction ID:</strong> #{reversalModal.item.id}</div>
              <div><strong>Player:</strong> {reversalModal.item.user_name || "User"} (UID: {reversalModal.item.user_id})</div>
              <div>
                <strong>Amount to Refund:</strong>{" "}
                <span className="font-bold text-emerald-700 text-sm">
                  {/gc/i.test(reversalModal.item.remarks || "") ? "🪙" : "✨"} {Number(reversalModal.item.amount).toLocaleString()} {/gc/i.test(reversalModal.item.remarks || "") ? "GC" : "SC"}
                </span>
              </div>
              <div className="text-gray-600"><strong>Original Remarks:</strong> {reversalModal.item.remarks}</div>
            </div>

            <div className="mb-5">
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Reason for Reversal / Correction (Mandatory)
              </label>
              <textarea
                rows={3}
                className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-amber-500"
                placeholder="e.g., Unintended deduction correction, resolved dispute, mistaken UID..."
                value={reversalModal.reason}
                onChange={(e) => setReversalModal(prev => ({ ...prev, reason: e.target.value }))}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                disabled={reversing}
                onClick={() => setReversalModal({ isOpen: false, item: null, reason: "" })}
                className="px-4 py-2 border rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={reversing || reversalModal.reason.trim().length < 5}
                onClick={handleConfirmReversal}
                className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-lg text-sm font-bold shadow transition disabled:opacity-50"
              >
                {reversing ? "Processing Refund..." : "Confirm & Refund Player"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BonusAndPaneltyHistory;
